import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import useAuditLog from '@/composables/useAuditLog'

export default function useTreatmentAdd() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router } = useRouter()
  const { createItem, updateItem } = useLambda()
  const { getLastCode } = useMongoDBFunctions()
  const { createInitialAuditEntry } = useAuditLog()

  const userData = store.state?.userStore?.userData
  const isSubmitting = ref(false)
  const collection = 'treatment'
  const treatmentData = ref({
    status: 'draft',
    tiposDatos: [],
    hasSensitive: false,
    hasMinors: false,
    hasBiometric: false,
    terceros: {},
    encargados: {},
    transferencias: {},
    resguardos: {},
  })
  const treatmentId = ref(null) // Store the created treatment ID for updates

  // Build payload helper
  const buildPayload = (data, status = 'draft') => ({
    client_id: oid(userData.client.$oid),
    nombre: data.nombre,
    empresa_id: data.empresa_id ? oid(data.empresa_id) : null,
    rol: data.rol,
    departamento_id: data.departamento_id ? oid(data.departamento_id) : null,
    responsable_id: data.responsable_id ? oid(data.responsable_id) : null,
    finalidades: data.finalidades,
    legitimacion: data.legitimacion,
    mecanismoConsentimiento: data.mecanismoConsentimiento || '',
    tiposDatos: data.tiposDatos || [],
    hasSensitive: data.hasSensitive,
    hasMinors: data.hasMinors,
    hasBiometric: data.hasBiometric,
    excepcionSensible: data.excepcionSensible || '',
    sensiblesDescripcion: data.sensiblesDescripcion || '',
    periodicidad: data.periodicidad,
    origen: data.origen,
    categoriaInteresados: data.categoriaInteresados || '',
    medios: data.medios || '',
    volumen: data.volumen,
    soporte: data.soporte || '',
    formato: data.formato || '',
    ambito: data.ambito,
    plazoConservacion: data.plazoConservacion,
    terceros: {
      fuenteCedente: data.terceros?.fuenteCedente || '',
      fuenteRegularizacion: data.terceros?.fuenteRegularizacion || '',
      destinatario: data.terceros?.destinatario || '',
      destinatarioFinalidad: data.terceros?.destinatarioFinalidad || '',
      destinatarioLegitimacion: data.terceros?.destinatarioLegitimacion || '',
    },
    encargados: {
      identificacion: data.encargados?.identificacion || '',
      prestacion: data.encargados?.prestacion || '',
    },
    transferencias: {
      destinatario: data.transferencias?.destinatario || '',
      pais: data.transferencias?.pais || '',
    },
    resguardos: {
      papel: data.resguardos?.papel || '',
      sistemas: data.resguardos?.sistemas || '',
    },
    observaciones: data.observaciones || '',
    status,
  })

  // Save as draft (for autosave and step navigation)
  // NO registra en audit log porque es autosave automático, no acción del usuario
  const saveDraft = async (data) => {
    try {
      // If treatment doesn't exist yet, create it
      if (!treatmentId.value) {
        // Generate treatmentId using getLastCode (T-001, T-002, etc.)
        const query = {
          collection,
          client_id: userData.client.$oid
        }
        const lastCode = await getLastCode(query)
        const newCode = lastCode + 1
        const newTreatmentId = `T-${String(newCode).padStart(3, '0')}`

        const payload = {
          ...buildPayload(data, 'draft'),
          treatmentId: newTreatmentId,
          history: [], // Inicializar vacío, el log se crea solo al presionar submit final
        }

        const { insertedId } = await createItem({ collection, payload })
        treatmentId.value = insertedId

        // Update treatmentData with the generated ID
        treatmentData.value.treatmentId = newTreatmentId
        treatmentData.value._id = insertedId

        return insertedId
      } else {
        // Update existing draft - sin audit log (es autosave)
        const query = { _id: oid(treatmentId.value) }
        const action = { $set: buildPayload(data, 'draft') }

        await updateItem({ collection, query, action })

        return treatmentId.value
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final submit (marks as active)
  // Solo registra 1 log: "Creación" cuando el usuario presiona submit final
  const create = async (data) => {
    if (!hasPermission('privacy-treatment', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      // If draft exists, update it to active and add audit entry
      if (treatmentId.value) {
        const payload = buildPayload(data, 'active')
        const action = {
          $set: payload,
          $push: { history: createInitialAuditEntry() }, // Solo 1 log: "Creación"
        }

        const query = { _id: oid(treatmentId.value) }
        await updateItem({ collection, query, action })
      } else {
        // Create new treatment directly as active
        const query = {
          collection,
          client_id: userData.client.$oid
        }
        const lastCode = await getLastCode(query)
        const newCode = lastCode + 1
        const newTreatmentId = `T-${String(newCode).padStart(3, '0')}`

        const payload = {
          ...buildPayload(data, 'active'),
          treatmentId: newTreatmentId,
          history: [createInitialAuditEntry()], // Solo 1 log: "Creación"
        }

        await createItem({ collection, payload })
      }

      showSuccessMessage('Tratamiento creado correctamente')
      router.push({ name: 'privacy-treatment-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al crear tratamiento')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    create,
    saveDraft,
    treatmentData,
    isSubmitting,
    treatmentId,
  }
}
